import { useEffect, useMemo, useState } from 'react';

import { useQueryParams } from 'use-query-params';

import type { GridFilterItem } from '@packages/fenrir/components';
import { type GridFilterModel } from '@packages/fenrir/components';

import { getParam } from '../../filter-management/filter-parameters';
import type { allRisksQueryParamType } from '../../pages/all-risks/all-risks-table.constants';
import { getInitialFilterState } from '../../pages/risks/grid-state/get-initial-filters-state';
import type { vulnQueryParamType } from '../../pages/vulnerabilities/vuln-table.constants';

import { type defaultFilter } from './filter-types';

const MAX_SEARCH_LENGTH = 50;

type ParamValue = string | number | boolean | null | undefined | string[];

type GetValueReturnType =
  | string
  | string[]
  | (undefined | number)[]
  | number
  | undefined
  | string[]
  | (number | undefined)[];

// Better typed interface for filter map items
export interface GenericFilterMapItem<P extends string> {
  column: string;
  operator: string;
  getValue: (value: string | string[]) => GetValueReturnType;
  paramKey: P | P[];
}

const getParamValue = <P extends string>(
  filterItem: GenericFilterMapItem<P>,
): P | P[] => {
  return filterItem.paramKey;
};

const isParamValueArray = <P extends string>(value: P | P[]): value is P[] => {
  return Array.isArray(value);
};

const handleRangeFilter = <P extends string>(
  value: string,
  key: string,
  gridFilterModel: GridFilterModel,
  filterItem: GenericFilterMapItem<P>,
) => {
  const gridFilterItem = gridFilterModel.items.find(
    (item) => item.field === filterItem.column,
  );

  const paramValue = getParamValue(filterItem);
  const paramIndex = isParamValueArray(paramValue)
    ? paramValue.findIndex((param) => param === key)
    : 0;

  const safeIndex = paramIndex >= 0 ? paramIndex : 0;

  if (gridFilterItem) {
    gridFilterItem.value[safeIndex] = value;
    return {
      ...gridFilterModel,
    };
  } else {
    const newItem: GridFilterItem = {
      value: [undefined, undefined],
      field: filterItem.column,
      operator: filterItem.operator,
      id: gridFilterModel.items.length + 1,
    };
    newItem.value[safeIndex] = value;
    return {
      ...gridFilterModel,
      ...{ items: [...gridFilterModel.items, newItem] },
    };
  }
};

type queryParamType = vulnQueryParamType | allRisksQueryParamType;

export const useRisksManagementFilters = <
  T extends Record<string, ParamValue>,
  P extends string = string,
>(
  queryParamsMap: queryParamType,
  defaultFilter: defaultFilter,
  options: {
    filtersMap: GenericFilterMapItem<P>[];
    gridStateKey: string;
  },
) => {
  const filtersMap = options.filtersMap;
  const gridStateKey = options.gridStateKey;
  const [queryParams, setQueryParams] = useQueryParams(queryParamsMap);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    // check if we have any valid query params to use a defaults for filters
    const initialFilterState: GridFilterModel = Object.entries(
      queryParams,
    ).reduce(
      (gridFilterModel: GridFilterModel, [key, value]) => {
        if (value !== 0 && !value) return gridFilterModel;
        const filterItem = filtersMap.find((f) => {
          const paramValue = getParamValue(f);
          if (isParamValueArray(paramValue)) {
            return paramValue.find((i) => i === key);
          } else {
            return paramValue === key;
          }
        });
        if (!filterItem) return gridFilterModel; // something in the URL we don't support

        if (filterItem.column === 'search') {
          return {
            ...gridFilterModel,
            ...{
              quickFilterValues:
                typeof value === 'string'
                  ? value.substring(0, MAX_SEARCH_LENGTH).split(' ')
                  : [],
            },
          };
        }

        const paramValue = getParamValue(filterItem);
        if (isParamValueArray(paramValue)) {
          return handleRangeFilter<P>(value, key, gridFilterModel, filterItem);
        }

        return {
          ...gridFilterModel,
          ...{
            items: [
              ...gridFilterModel.items,
              {
                value,
                field: filterItem.column,
                operator: filterItem.operator,
                id: gridFilterModel.items.length + 1,
              },
            ],
          },
        };
      },
      { items: [] },
    );
    if (initialFilterState.items.length > 0) return initialFilterState;
    // fallback to using session storage for defaults, else we will use preset defaults
    return getInitialFilterState(gridStateKey, {
      items: defaultFilter,
    });
  });

  const params = useMemo(() => {
    let result = {} as T;
    if (
      filterModel?.quickFilterValues &&
      filterModel?.quickFilterValues.length > 0
    ) {
      result = {
        ...result,
        search: filterModel.quickFilterValues
          .join(' ')
          .substring(0, MAX_SEARCH_LENGTH),
      };
    }

    filtersMap.forEach((filterItem) => {
      const filterValue = filterItem.getValue(
        getParam(filterItem.column, filterModel, filterItem.operator),
      );

      if (filterValue) {
        //handle range filters
        const paramValue = getParamValue(filterItem);
        if (isParamValueArray(paramValue)) {
          if (Array.isArray(filterValue))
            paramValue.forEach((param, index) => {
              result = {
                ...result,
                [param]: filterValue[index],
              };
            });
        } else {
          result = {
            ...result,
            [paramValue]: filterValue,
          };
        }
      }
    });
    return result;
  }, [filterModel, filtersMap]);

  useEffect(() => {
    let newQueryParams: Record<string, ParamValue> = {};
    filtersMap.forEach((filterItem) => {
      //handle range filters
      const paramValues = filterItem.paramKey;
      if (Array.isArray(paramValues)) {
        paramValues.forEach((param) => {
          const value = params[param];
          newQueryParams = {
            ...newQueryParams,
            [param]: value,
          };
        });
      } else {
        const value = params[paramValues as keyof T];

        newQueryParams = {
          ...newQueryParams,
          [paramValues]: value,
        };
      }
    });
    setQueryParams(newQueryParams, 'replaceIn');
  }, [params, setQueryParams, filtersMap]);

  return { filterModel, setFilterModel, riskParams: params };
};
